import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import {
  CalendarCheck, Plus, Search, Printer, CheckCircle, XCircle, Clock, Stethoscope,
  MapPin, Filter, Trash2, Paperclip, Upload, Download, FileText, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import DictableTextarea from '@/components/DictableTextarea';
import { openPrintWindow } from '@/lib/letterhead';
import DatePickerField from '@/components/DatePickerField';

const tipiVisitaLabels = {
  prelievo_ematico: 'Prelievo Ematico',
  visita_cardiologica: 'Visita Cardiologica',
  visita_ortopedica: 'Visita Ortopedica',
  visita_neurologica: 'Visita Neurologica',
  visita_dermatologica: 'Visita Dermatologica',
  visita_oculistica: 'Visita Oculistica',
  visita_odontoiatrica: 'Visita Odontoiatrica',
  visita_urologica: 'Visita Urologica',
  visita_ginecologica: 'Visita Ginecologica',
  visita_pneumologica: 'Visita Pneumologica',
  visita_diabetologica: 'Visita Diabetologica',
  visita_geriatrica: 'Visita Geriatrica',
  controllo_mmg: 'Controllo MMG',
  esame_diagnostico: 'Esame Diagnostico',
  ecografia: 'Ecografia',
  radiografia: 'Radiografia',
  tac: 'TAC',
  risonanza: 'Risonanza Magnetica',
  fisioterapia: 'Fisioterapia',
  logopedia: 'Logopedia',
  altro: 'Altro',
};

const statoMap = {
  programmato: { label: 'Programmato', class: 'bg-blue-100 text-blue-700', icon: Clock },
  effettuato: { label: 'Effettuato', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  annullato: { label: 'Annullato', class: 'bg-red-100 text-red-700', icon: XCircle },
};

export default function Appuntamenti() {
  const { hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [ospiti, setOspiti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('tutti');
  const [filtroStato, setFiltroStato] = useState('tutti');
  const [filtroArea, setFiltroArea] = useState('tutti');
  const [filtroDataDa, setFiltroDataDa] = useState('');
  const [filtroDataA, setFiltroDataA] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showConferma, setShowConferma] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showDettaglio, setShowDettaglio] = useState(false);
  const [allegati, setAllegati] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [allegFile, setAllegFile] = useState(null);
  const [allegNome, setAllegNome] = useState('');
  const [allegTipo, setAllegTipo] = useState('referto');
  const [allegNote, setAllegNote] = useState('');
  const [ospiteSearch, setOspiteSearch] = useState('');
  const [form, setForm] = useState({
    ospite_id: '', data_ora: '', tipo: 'prelievo_ematico', specialista: '',
    luogo: '', luogo_tipo: 'interno', note: '',
    ricorrente: false, ricorrenza_tipo: 'settimanale', ricorrenza_settimane: 4
  });
  const [confermaForm, setConfermaForm] = useState({ esito: '', note_esito: '' });

  const fetchAppuntamenti = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (filtroTipo !== 'tutti') params.tipo = filtroTipo;
    if (filtroStato !== 'tutti') params.stato = filtroStato;
    if (filtroArea !== 'tutti') params.area = filtroArea;
    if (filtroDataDa) params.data_da = filtroDataDa;
    if (filtroDataA) params.data_a = filtroDataA;
    try {
      const res = await api.get('/appuntamenti', { params });
      setItems(res.data);
    } catch { setItems([]); }
    setLoading(false);
  }, [filtroTipo, filtroStato, filtroArea, filtroDataDa, filtroDataA]);

  useEffect(() => { fetchAppuntamenti(); }, [fetchAppuntamenti]);
  useEffect(() => {
    api.get('/ospiti', { params: { stato: 'presente' } }).then(r => setOspiti(r.data)).catch(() => {});
  }, []);

  const filtered = search
    ? items.filter(i => i.ospite_nome?.toLowerCase().includes(search.toLowerCase()) || (tipiVisitaLabels[i.tipo] || '').toLowerCase().includes(search.toLowerCase()))
    : items;

  const handleCreate = async () => {
    if (!form.ospite_id || !form.data_ora || !form.tipo) {
      toast.error('Compila ospite, data/ora e tipo visita');
      return;
    }
    try {
      const res = await api.post('/appuntamenti', form);
      if (res.data.appuntamenti_creati) {
        toast.success(`${res.data.appuntamenti_creati} appuntamenti programmati`);
      } else {
        toast.success('Appuntamento programmato');
      }
      setShowForm(false);
      setForm({ ospite_id: '', data_ora: '', tipo: 'prelievo_ematico', specialista: '', luogo: '', luogo_tipo: 'interno', note: '', ricorrente: false, ricorrenza_tipo: 'settimanale', ricorrenza_settimane: 4 });
      fetchAppuntamenti();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleConferma = async () => {
    try {
      await api.put(`/appuntamenti/${selectedApp.id}/conferma`, confermaForm);
      toast.success('Visita confermata');
      setShowConferma(false);
      fetchAppuntamenti();
    } catch { toast.error('Errore'); }
  };

  // Allegati
  const openDettaglio = async (app) => {
    setSelectedApp(app);
    setShowDettaglio(true);
    try { const res = await api.get(`/appuntamenti/${app.id}/allegati`); setAllegati(res.data); } catch { setAllegati([]); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File troppo grande (max 10MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => { setAllegFile(reader.result); setAllegNome(file.name); };
    reader.readAsDataURL(file);
  };

  const handleUploadAllegato = async () => {
    if (!allegFile || !selectedApp) return;
    setUploading(true);
    try {
      await api.post(`/appuntamenti/${selectedApp.id}/allegati`, {
        nome_file: allegNome, tipo: allegTipo, contenuto_base64: allegFile, note: allegNote
      });
      // Auto-mark as effettuato if still programmato
      if (selectedApp.stato === 'programmato') {
        try {
          await api.put(`/appuntamenti/${selectedApp.id}`, { stato: 'effettuato' });
        } catch { /* ignore if update fails */ }
      }
      toast.success('Allegato caricato');
      setAllegFile(null); setAllegNome(''); setAllegNote('');
      const res = await api.get(`/appuntamenti/${selectedApp.id}/allegati`);
      setAllegati(res.data);
      fetchAppuntamenti(); // Refresh list so item moves from programmati to storico
    } catch { toast.error('Errore upload'); }
    setUploading(false);
  };

  const handleDownloadAllegato = async (allegatoId, nomeFile) => {
    try {
      const res = await api.get(`/appuntamenti/allegati/${allegatoId}`);
      const link = document.createElement('a');
      link.href = res.data.contenuto_base64;
      link.download = nomeFile;
      link.click();
    } catch { toast.error('Errore download'); }
  };

  const handleDeleteAllegato = async (allegatoId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo allegato?')) return;
    try {
      await api.delete(`/appuntamenti/allegati/${allegatoId}`);
      toast.success('Allegato eliminato');
      const res = await api.get(`/appuntamenti/${selectedApp.id}/allegati`);
      setAllegati(res.data);
    } catch { toast.error('Errore'); }
  };

  const handleAnnulla = async (id) => {
    if (!window.confirm('Sei sicuro di voler annullare questo appuntamento?')) return;
    try {
      await api.put(`/appuntamenti/${id}`, { stato: 'annullato' });
      toast.success('Appuntamento annullato');
      fetchAppuntamenti();
    } catch { toast.error('Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo appuntamento?')) return;
    try {
      await api.delete(`/appuntamenti/${id}`);
      toast.success('Eliminato');
      fetchAppuntamenti();
    } catch { toast.error('Errore'); }
  };

  const openConferma = (app) => {
    setSelectedApp(app);
    setConfermaForm({ esito: '', note_esito: '' });
    setShowConferma(true);
  };

  const handlePrint = () => {
    const rows = filtered.map(a => {
      const s = statoMap[a.stato] || statoMap.programmato;
      const dt = new Date(a.data_ora);
      return `<tr style="${a.stato === 'effettuato' ? 'background:#f0fdf4' : a.stato === 'annullato' ? 'background:#fef2f2;color:#94a3b8' : ''}">
        <td>${dt.toLocaleDateString('it-IT')}</td>
        <td>${dt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</td>
        <td>${a.ospite_nome}</td>
        <td>${a.stanza || '-'}/${a.letto || '-'} ${a.area ? '(' + a.area + ')' : ''}</td>
        <td><strong>${tipiVisitaLabels[a.tipo] || a.tipo}</strong></td>
        <td>${a.specialista || '-'}</td>
        <td>${a.luogo || '-'} ${a.luogo_tipo === 'esterno' ? '(EST)' : ''}</td>
        <td>${s.label}</td>
        <td style="font-size:9px">${a.note || ''}</td>
      </tr>`;
    }).join('');

    let subtitle = 'Programmazione visite';
    if (filtroDataDa && filtroDataA) subtitle += ` dal ${filtroDataDa} al ${filtroDataA}`;
    else if (filtroDataDa) subtitle += ` dal ${filtroDataDa}`;
    if (filtroArea !== 'tutti') subtitle += ` - Area ${filtroArea}`;
    if (filtroTipo !== 'tutti') subtitle += ` - ${tipiVisitaLabels[filtroTipo] || filtroTipo}`;

    const body = `
      <div class="info-grid">
        <div class="info-item"><span class="info-label">Totale appuntamenti</span><span class="info-value">${filtered.length}</span></div>
        <div class="info-item"><span class="info-label">Programmati</span><span class="info-value">${filtered.filter(a => a.stato === 'programmato').length}</span></div>
        <div class="info-item"><span class="info-label">Effettuati</span><span class="info-value">${filtered.filter(a => a.stato === 'effettuato').length}</span></div>
      </div>
      <table><thead><tr><th>Data</th><th>Ora</th><th>Ospite</th><th>Camera/Area</th><th>Tipo Visita</th><th>Specialista</th><th>Luogo</th><th>Stato</th><th>Note</th></tr></thead><tbody>${rows}</tbody></table>`;
    openPrintWindow('Programmazione Visite Mediche', body, { subtitle });
  };

  const programmati = filtered.filter(a => a.stato === 'programmato').length;
  const effettuati = filtered.filter(a => a.stato === 'effettuato').length;

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto" data-testid="appuntamenti-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Cinzel] tracking-tight">Appuntamenti</h1>
          <p className="text-sm text-slate-500 mt-0.5">Programmazione visite mediche e appuntamenti</p>
          <div className="flex gap-2 mt-1">
            <Badge className="bg-blue-100 text-blue-700 text-[10px]">{programmati} programmati</Badge>
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{effettuati} effettuati</Badge>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint} data-testid="print-appuntamenti">
            <Printer className="w-4 h-4 mr-2" />Stampa
          </Button>
          {hasRole('coordinatore', 'medico', 'infermiere') && (
            <Button data-testid="add-appuntamento-btn" onClick={() => { setOspiteSearch(''); setForm(f => ({ ...f, ospite_id: '' })); setShowForm(true); }} className="bg-brand hover:bg-brand-dark text-white">
              <Plus className="w-4 h-4 mr-2" />Nuovo Appuntamento
            </Button>
          )}
        </div>
      </div>

      {/* Filtri */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Filter className="w-4 h-4 text-slate-400" />
            <div className="relative min-w-[200px] flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Cerca ospite o tipo visita..." className="pl-9 h-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="search-appuntamenti" />
            </div>
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger className="w-48 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti i tipi</SelectItem>
                {Object.entries(tipiVisitaLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filtroStato} onValueChange={setFiltroStato}>
              <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti gli stati</SelectItem>
                {Object.entries(statoMap).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filtroArea} onValueChange={setFiltroArea}>
              <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutte le aree</SelectItem>
                <SelectItem value="verde">Area Verde</SelectItem>
                <SelectItem value="gialla">Area Gialla</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <DatePickerField className="h-9 w-36" value={filtroDataDa} onChange={v => setFiltroDataDa(v)} />
              <span className="text-xs text-slate-400">-</span>
              <DatePickerField className="h-9 w-36" value={filtroDataA} onChange={v => setFiltroDataA(v)} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabella */}
      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="h-14 p-4" /></Card>)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16"><CalendarCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Nessun appuntamento</p></div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Data</TableHead>
                  <TableHead className="text-xs">Ora</TableHead>
                  <TableHead className="text-xs">Ospite</TableHead>
                  <TableHead className="text-xs">Camera / Area</TableHead>
                  <TableHead className="text-xs">Tipo Visita</TableHead>
                  <TableHead className="text-xs">Specialista</TableHead>
                  <TableHead className="text-xs">Luogo</TableHead>
                  <TableHead className="text-xs">Stato</TableHead>
                  <TableHead className="text-xs">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(a => {
                  const dt = new Date(a.data_ora);
                  const s = statoMap[a.stato] || statoMap.programmato;
                  const StIcon = s.icon;
                  const isPast = dt < new Date() && a.stato === 'programmato';
                  return (
                    <TableRow key={a.id}
                      className={a.stato === 'effettuato' ? 'bg-emerald-50/30' : a.stato === 'annullato' ? 'bg-slate-50 opacity-60' : isPast ? 'bg-amber-50/30' : ''}
                      data-testid={`appuntamento-row-${a.id}`}>
                      <TableCell className="text-sm font-medium">{dt.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}</TableCell>
                      <TableCell className="text-sm font-bold">{dt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</TableCell>
                      <TableCell>
                        <p className="text-sm font-medium">{a.ospite_nome}</p>
                      </TableCell>
                      <TableCell className="text-sm">
                        {a.stanza || '-'}/{a.letto || '-'}
                        {a.area && <Badge variant="outline" className="text-[9px] ml-1 capitalize">{a.area}</Badge>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px]"><Stethoscope className="w-3 h-3 mr-0.5" />{tipiVisitaLabels[a.tipo] || a.tipo}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{a.specialista || '-'}</TableCell>
                      <TableCell className="text-xs">
                        {a.luogo || '-'}
                        {a.luogo_tipo === 'esterno' && <Badge className="ml-1 text-[9px] bg-purple-100 text-purple-600"><MapPin className="w-2.5 h-2.5 mr-0.5" />EST</Badge>}
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-[10px] ${s.class}`}><StIcon className="w-3 h-3 mr-0.5" />{s.label}</Badge>
                        {a.stato === 'effettuato' && a.esito && <p className="text-[9px] text-slate-500 mt-0.5">{a.esito}</p>}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {a.stato === 'programmato' && hasRole('coordinatore', 'medico', 'infermiere') && (
                            <>
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-600" onClick={() => openConferma(a)} data-testid={`conferma-app-${a.id}`}>
                                <CheckCircle className="w-3.5 h-3.5 mr-1" />Conferma
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500" onClick={() => handleAnnulla(a.id)} data-testid={`annulla-app-${a.id}`}>
                                <XCircle className="w-3.5 h-3.5 mr-1" />Annulla
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-brand" onClick={() => openDettaglio(a)} data-testid={`dettaglio-app-${a.id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" />{a.stato === 'effettuato' ? 'Allegati' : 'Dettaglio'}
                          </Button>
                          {hasRole('coordinatore', 'medico') && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDelete(a.id)} title="Elimina">
                              <Trash2 className="w-3.5 h-3.5 text-slate-400" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Form nuovo appuntamento */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarCheck className="w-5 h-5 text-brand" />Nuovo Appuntamento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Ospite *</Label>
              <div className="relative">
                <Input
                  data-testid="app-ospite"
                  placeholder="Cerca per nome, cognome..."
                  value={ospiteSearch}
                  onChange={e => {
                    setOspiteSearch(e.target.value);
                    if (!e.target.value) setForm(f => ({ ...f, ospite_id: '' }));
                  }}
                />
                {ospiteSearch && !form.ospite_id && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {ospiti.filter(o => `${o.cognome} ${o.nome} ${o.stanza}`.toLowerCase().includes(ospiteSearch.toLowerCase())).slice(0, 10).map(o => (
                      <button key={o.id} className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex justify-between" onClick={() => {
                        setForm(f => ({ ...f, ospite_id: o.id }));
                        setOspiteSearch(`${o.cognome} ${o.nome} - Cam. ${o.stanza}/${o.letto}`);
                      }}>
                        <span className="font-medium">{o.cognome} {o.nome}</span>
                        <span className="text-xs text-slate-400">Cam. {o.stanza}/{o.letto}</span>
                      </button>
                    ))}
                    {ospiti.filter(o => `${o.cognome} ${o.nome} ${o.stanza}`.toLowerCase().includes(ospiteSearch.toLowerCase())).length === 0 && (
                      <p className="px-3 py-2 text-xs text-slate-400">Nessun ospite trovato</p>
                    )}
                  </div>
                )}
                {form.ospite_id && (
                  <button className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500 text-xs" onClick={() => { setForm(f => ({ ...f, ospite_id: '' })); setOspiteSearch(''); }}>
                    &times;
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Data e Ora *</Label>
                <Input type="datetime-local" data-testid="app-datetime" value={form.data_ora} onChange={e => setForm(f => ({ ...f, data_ora: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Tipo Visita *</Label>
                <Select value={form.tipo} onValueChange={v => setForm(f => ({ ...f, tipo: v }))}>
                  <SelectTrigger data-testid="app-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(tipiVisitaLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Specialista / Medico</Label>
                <Input data-testid="app-specialista" value={form.specialista} onChange={e => setForm(f => ({ ...f, specialista: e.target.value }))} placeholder="Dr. Rossi" />
              </div>
              <div>
                <Label className="text-xs">Luogo</Label>
                <Input value={form.luogo} onChange={e => setForm(f => ({ ...f, luogo: e.target.value }))} placeholder="Ambulatorio, Ospedale..." />
              </div>
            </div>
            <div>
              <Label className="text-xs">Tipo Luogo</Label>
              <Select value={form.luogo_tipo} onValueChange={v => setForm(f => ({ ...f, luogo_tipo: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="interno">Interno (in struttura)</SelectItem>
                  <SelectItem value="esterno">Esterno (fuori struttura)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Note</Label>
              <DictableTextarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={2} placeholder="Note aggiuntive..." />
            </div>
            {/* Ricorrenza */}
            <div className="border rounded-lg p-3 bg-slate-50 space-y-3">
              <div className="flex items-center gap-3">
                <Switch checked={form.ricorrente} onCheckedChange={v => setForm(f => ({ ...f, ricorrente: v }))} data-testid="app-ricorrente" />
                <Label className="text-xs font-medium">Appuntamento ricorrente</Label>
              </div>
              {form.ricorrente && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Frequenza</Label>
                    <Select value={form.ricorrenza_tipo} onValueChange={v => setForm(f => ({ ...f, ricorrenza_tipo: v }))}>
                      <SelectTrigger data-testid="app-ricorrenza-tipo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="settimanale">Settimanale</SelectItem>
                        <SelectItem value="bisettimanale">Ogni 2 settimane</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Per quante settimane</Label>
                    <Input type="number" min="2" max="52" value={form.ricorrenza_settimane} onChange={e => setForm(f => ({ ...f, ricorrenza_settimane: parseInt(e.target.value) || 4 }))} data-testid="app-ricorrenza-settimane" />
                  </div>
                  <div className="col-span-2">
                    <p className="text-[10px] text-brand">
                      Verranno creati {form.ricorrenza_settimane} appuntamenti{' '}
                      ({form.ricorrenza_tipo === 'settimanale' ? 'ogni settimana' : 'ogni 2 settimane'})
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
              <Button data-testid="save-appuntamento" onClick={handleCreate} className="bg-brand hover:bg-brand-dark text-white">Programma</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma visita */}
      <Dialog open={showConferma} onOpenChange={setShowConferma}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-emerald-600" />Conferma Visita Effettuata</DialogTitle></DialogHeader>
          {selectedApp && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                <p className="font-semibold">{selectedApp.ospite_nome}</p>
                <p className="text-xs text-slate-500">{tipiVisitaLabels[selectedApp.tipo]} - {new Date(selectedApp.data_ora).toLocaleString('it-IT')}</p>
                {selectedApp.specialista && <p className="text-xs text-slate-500">Specialista: {selectedApp.specialista}</p>}
              </div>
              <div>
                <Label className="text-xs">Esito della Visita</Label>
                <DictableTextarea data-testid="conferma-esito" value={confermaForm.esito} onChange={e => setConfermaForm(f => ({ ...f, esito: e.target.value }))} rows={2} placeholder="Esame nella norma, prescritta terapia..." />
              </div>
              <div>
                <Label className="text-xs">Note Aggiuntive</Label>
                <DictableTextarea value={confermaForm.note_esito} onChange={e => setConfermaForm(f => ({ ...f, note_esito: e.target.value }))} rows={2} placeholder="Follow-up, prescrizioni..." />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowConferma(false)}>Annulla</Button>
                <Button data-testid="save-conferma" onClick={handleConferma} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <CheckCircle className="w-4 h-4 mr-1" />Conferma Effettuata
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog dettaglio + allegati */}
      <Dialog open={showDettaglio} onOpenChange={setShowDettaglio}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Paperclip className="w-5 h-5 text-brand" />Dettaglio Appuntamento</DialogTitle></DialogHeader>
          {selectedApp && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold">{selectedApp.ospite_nome}</p>
                  <Badge className={`text-[10px] ${(statoMap[selectedApp.stato] || statoMap.programmato).class}`}>{(statoMap[selectedApp.stato] || statoMap.programmato).label}</Badge>
                </div>
                <p className="text-xs text-slate-500">{tipiVisitaLabels[selectedApp.tipo]} - {new Date(selectedApp.data_ora).toLocaleString('it-IT')}</p>
                {selectedApp.specialista && <p className="text-xs text-slate-500">Specialista: {selectedApp.specialista}</p>}
                {selectedApp.luogo && <p className="text-xs text-slate-500">Luogo: {selectedApp.luogo} {selectedApp.luogo_tipo === 'esterno' ? '(Esterno)' : ''}</p>}
                {selectedApp.esito && <p className="text-xs text-emerald-600 mt-1"><strong>Esito:</strong> {selectedApp.esito}</p>}
                {selectedApp.note_esito && <p className="text-xs text-slate-500"><strong>Note:</strong> {selectedApp.note_esito}</p>}
                {selectedApp.confermato_da && <p className="text-[10px] text-slate-400 mt-1">Confermato da: {selectedApp.confermato_da}</p>}
              </div>

              {/* Lista allegati */}
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5" />Allegati ({allegati.length})
                </h4>
                {allegati.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-4">Nessun allegato</p>
                ) : (
                  <div className="space-y-2">
                    {allegati.map(a => (
                      <div key={a.id} className="flex items-center justify-between p-2 border rounded-lg bg-white" data-testid={`allegato-${a.id}`}>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium">{a.nome_file}</p>
                            <p className="text-[10px] text-slate-400">
                              {a.tipo} - {a.uploaded_by_name} - {new Date(a.uploaded_at).toLocaleDateString('it-IT')}
                            </p>
                            {a.note && <p className="text-[10px] text-slate-500">{a.note}</p>}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownloadAllegato(a.id, a.nome_file)} data-testid={`download-allegato-${a.id}`} title="Scarica">
                            <Download className="w-3.5 h-3.5 text-blue-500" />
                          </Button>
                          {hasRole('coordinatore', 'medico') && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDeleteAllegato(a.id)} title="Elimina">
                              <Trash2 className="w-3.5 h-3.5 text-slate-400" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Upload form */}
              {hasRole('coordinatore', 'medico', 'infermiere') && (
                <div className="border-t pt-4">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Upload className="w-3.5 h-3.5" />Carica Allegato
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs">File (PDF, immagini, max 10MB)</Label>
                      <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleFileSelect} data-testid="allegato-file-input" className="text-xs" />
                    </div>
                    {allegFile && (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Tipo documento</Label>
                            <Select value={allegTipo} onValueChange={setAllegTipo}>
                              <SelectTrigger data-testid="allegato-tipo"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="referto">Referto</SelectItem>
                                <SelectItem value="esame_sangue">Esame del Sangue</SelectItem>
                                <SelectItem value="radiografia">Radiografia/Immagine</SelectItem>
                                <SelectItem value="prescrizione">Prescrizione</SelectItem>
                                <SelectItem value="lettera">Lettera Specialista</SelectItem>
                                <SelectItem value="altro">Altro</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Note</Label>
                            <Input value={allegNote} onChange={e => setAllegNote(e.target.value)} placeholder="Descrizione..." className="text-xs" />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">{allegNome}</Badge>
                          <Button size="sm" onClick={handleUploadAllegato} disabled={uploading} data-testid="upload-allegato-btn" className="bg-brand hover:bg-brand-dark text-white h-8 text-xs">
                            <Upload className="w-3.5 h-3.5 mr-1" />{uploading ? 'Caricamento...' : 'Carica'}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
